import { makeAutoObservable, runInAction } from "mobx";
import { IsNotEmpty, IsString, validate } from "class-validator";
import {
  ApiError,
  superadminCommentHelpOrder,
  superadminGetHelpOrder,
  superadminHelpOrdersMetrics,
  superadminListHelpExecutors,
  superadminListHelpOrders,
  superadminListHelpServices,
  superadminNotifyHelpOrder,
  superadminUpdateHelpOrder,
  superadminUpdateHelpOrderStatus,
} from "@pro-sto/api-client";
import type {
  HelpOrdersMetricsDto,
  SuperadminHelpOrderListItemDto,
  UpdateHelpOrderDto,
  UpdateHelpOrderStatusDto,
} from "@pro-sto/api-client";
import { toastStore } from "./ToastStore";
import {
  deriveBlockedWheels,
  deriveHardToAccess,
  mapHelpExecutorProfile,
  mapHelpOrderDetail,
  mapHelpService,
  executorPatchSnapshot,
  toExecutorPatch,
  toItemPatch,
  toPointDto,
  unwrapHelpList,
  unwrapHelpPayload,
} from "@/pages/HelpPage/helpMappers";
import type {
  HelpExecutor,
  HelpExecutorProfile,
  HelpLineItem,
  HelpOrderDetail,
  HelpPoint,
  HelpService,
} from "@/pages/HelpPage/types";
import {
  blankExecutorAssignment,
  extraOnSiteAmount,
  orderTotal,
} from "@/pages/HelpPage/types";
import { toStoredHelpPhone } from "@/pages/HelpPage/helpPhone";
import {
  superadminSendHelpOrderToLat,
  superadminSyncHelpOrderFromLat,
} from "@/pages/HelpPage/helpLatApi";

export type HelpServiceKind = SuperadminHelpOrderListItemDto["kind"];
export type HelpOrderStatus =
  | SuperadminHelpOrderListItemDto["status"]
  | "awaiting_payment";
export type HelpPaymentStatus = SuperadminHelpOrderListItemDto["prepaid_status"];
export type HelpProvider = "internal" | "lat";

export type HelpOrderStatusUpdatable = UpdateHelpOrderStatusDto["status"];

export type HelpOrderListItem = SuperadminHelpOrderListItemDto & {
  provider: HelpProvider;
  status: HelpOrderStatus;
};

export type HelpMetricsView = HelpOrdersMetricsDto & {
  through_lat: number;
};

export type HelpOrderSaveDraft = {
  customer: {
    clientName: string;
    clientPhone: string;
    vehicle: {
      makeModel: string;
      regNumber: string;
      vin: string;
      year: string;
    };
  };
  pickup: HelpPoint;
  dropoff: HelpPoint | null;
  executor: HelpExecutor | null;
  items: HelpLineItem[];
  prepaidAmount: number;
  prepaidStatus: HelpPaymentStatus;
  extraPaymentStatus: HelpPaymentStatus;
};

type HelpOrderUpdateBody = UpdateHelpOrderDto & {
  prepaid_amount?: number;
};

class HelpCommentDto {
  @IsString()
  @IsNotEmpty({ message: "Введите комментарий" })
  text = "";
}

const HELP_KINDS: HelpServiceKind[] = ["tow", "fuel", "jump_start"];
const HELP_STATUSES: HelpOrderStatus[] = [
  "awaiting_payment",
  "awaiting_confirmation",
  "en_route",
  "on_site",
  "in_progress",
  "completed",
  "cancelled",
];
const HELP_PAYMENTS: HelpPaymentStatus[] = ["pending", "success", "refunded"];

const emptyMetrics = (): HelpMetricsView => ({
  active: 0,
  awaiting_dispatcher: 0,
  prepaid_sum: 0,
  through_lat: 0,
});

let helpWriteChain: Promise<void> = Promise.resolve();

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function pickString(record: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") return value;
  }
  return "";
}

function pickNumber(record: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return 0;
}

function asKind(value: string): HelpServiceKind {
  return HELP_KINDS.includes(value as HelpServiceKind)
    ? (value as HelpServiceKind)
    : "tow";
}

function asStatus(value: string): HelpOrderStatus {
  return HELP_STATUSES.includes(value as HelpOrderStatus)
    ? (value as HelpOrderStatus)
    : "awaiting_confirmation";
}

function asPayment(value: string): HelpPaymentStatus {
  return HELP_PAYMENTS.includes(value as HelpPaymentStatus)
    ? (value as HelpPaymentStatus)
    : "pending";
}

function asProvider(value: string): HelpProvider {
  return value === "lat" ? "lat" : "internal";
}

function parseMetrics(raw: unknown): HelpMetricsView {
  const record = asRecord(raw);
  if (!record) return emptyMetrics();
  return {
    active: pickNumber(record, "active"),
    awaiting_dispatcher: pickNumber(
      record,
      "awaiting_dispatcher",
      "awaitingDispatcher",
    ),
    prepaid_sum: pickNumber(record, "prepaid_sum", "prepaidSum"),
    through_lat: pickNumber(
      record,
      "through_lat",
      "throughLat",
      "lat_count",
      "latCount",
    ),
  };
}

function parseOrder(raw: unknown): HelpOrderListItem | null {
  const record = asRecord(raw);
  if (!record) return null;
  const uuid = pickString(record, "uuid");
  if (!uuid) return null;
  return {
    uuid,
    created_at: pickString(record, "created_at", "createdAt"),
    client_name: pickString(record, "client_name", "clientName"),
    client_phone: pickString(record, "client_phone", "clientPhone"),
    vehicle_reg_number: pickString(
      record,
      "vehicle_reg_number",
      "vehicleRegNumber",
    ),
    kind: asKind(pickString(record, "kind")),
    service_title: pickString(record, "service_title", "serviceTitle"),
    status: asStatus(pickString(record, "status")),
    total_amount: pickNumber(record, "total_amount", "totalAmount"),
    prepaid_status: asPayment(
      pickString(record, "prepaid_status", "prepaidStatus"),
    ),
    provider: asProvider(pickString(record, "provider")),
  };
}

function countLatOrders(items: HelpOrderListItem[], kind: HelpServiceKind): number {
  return items.filter((item) => item.kind === kind && item.provider === "lat")
    .length;
}

function parseListResponse(raw: unknown): {
  items: HelpOrderListItem[];
  metrics: HelpMetricsView | null;
} {
  const record = asRecord(raw);
  const source = Array.isArray(raw)
    ? raw
    : record
      ? ((record["data"] as unknown[] | undefined) ??
        (record["items"] as unknown[] | undefined) ??
        [])
      : [];
  const items = source
    .map(parseOrder)
    .filter((item): item is HelpOrderListItem => item !== null);
  const metrics = record
    ? parseMetrics(record["metrics"] ?? record)
    : null;
  const hasMetrics =
    metrics !== null &&
    Boolean(asRecord(record?.["metrics"] ?? null));
  return { items, metrics: hasMetrics ? metrics : null };
}

function cloneDraft(draft: HelpOrderSaveDraft): HelpOrderSaveDraft {
  return {
    customer: {
      clientName: draft.customer.clientName,
      clientPhone: draft.customer.clientPhone,
      vehicle: { ...draft.customer.vehicle },
    },
    pickup: { ...draft.pickup },
    dropoff: draft.dropoff ? { ...draft.dropoff } : null,
    executor: draft.executor ? { ...draft.executor } : null,
    items: draft.items.map((item) => ({ ...item })),
    prepaidAmount: draft.prepaidAmount,
    prepaidStatus: draft.prepaidStatus,
    extraPaymentStatus: draft.extraPaymentStatus,
  };
}

function createEditDraft(order: HelpOrderDetail): HelpOrderSaveDraft {
  return {
    customer: {
      clientName: order.clientName,
      clientPhone: toStoredHelpPhone(order.clientPhone) || order.clientPhone,
      vehicle: {
        makeModel: order.vehicle.makeModel,
        regNumber: order.vehicle.regNumber,
        vin: order.vehicle.vin ?? "",
        year: order.vehicle.year ?? "",
      },
    },
    pickup: { ...order.pickup },
    dropoff: order.dropoff ? { ...order.dropoff } : null,
    executor: order.executor ? { ...order.executor } : blankExecutorAssignment(),
    items: order.items.map((item) => ({ ...item })),
    prepaidAmount: order.prepaidAmount,
    prepaidStatus: order.prepaidStatus,
    extraPaymentStatus: order.extraPaymentStatus,
  };
}

function snapshotKey(value: unknown): string {
  return JSON.stringify(value);
}

function overlayDraftOnDetail(
  detail: HelpOrderDetail,
  draft: HelpOrderSaveDraft,
): HelpOrderDetail {
  const items = draft.items.filter((item) => item.title.trim());
  const executor = draft.executor?.name.trim()
    ? { ...draft.executor, name: draft.executor.name.trim() }
    : null;
  return {
    ...detail,
    clientName: draft.customer.clientName,
    clientPhone: draft.customer.clientPhone,
    vehicle: {
      makeModel: draft.customer.vehicle.makeModel,
      regNumber: draft.customer.vehicle.regNumber,
      vin: draft.customer.vehicle.vin.trim() || null,
      year: draft.customer.vehicle.year.trim() || null,
    },
    pickup: draft.pickup,
    dropoff: draft.dropoff,
    executor,
    items,
    prepaidAmount: draft.prepaidAmount,
    prepaidStatus: draft.prepaidStatus,
    extraPaymentStatus: draft.extraPaymentStatus,
    extraAmount: extraOnSiteAmount({
      items,
      prepaidAmount: draft.prepaidAmount,
    }),
    totalAmount: orderTotal({ items }),
  };
}

function extractApiError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = asRecord(err.body);
    const message = body?.["message"];
    if (typeof message === "string" && message.length > 0) return message;
    if (Array.isArray(message) && typeof message[0] === "string") {
      return message[0];
    }
  }
  return fallback;
}

function collectApiErrorText(error: unknown): string {
  if (!(error instanceof ApiError)) return "";
  const body = asRecord(error.body);
  const message = body?.["message"];
  if (typeof message === "string") return message;
  if (Array.isArray(message)) {
    return message
      .filter((item): item is string => typeof item === "string")
      .join(" ");
  }
  return "";
}

/** Повтор без prepaid_amount только если ошибка явно про это поле */
function isPrepaidAmountUnsupportedError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.status !== 400 && error.status !== 422) return false;

  const haystack = collectApiErrorText(error).toLowerCase();
  return (
    haystack.includes("prepaid_amount") ||
    haystack.includes("prepaid amount") ||
    haystack.includes("prepaidamount")
  );
}

class HelpStore {
  items: HelpOrderListItem[] = [];
  metrics: HelpMetricsView = emptyMetrics();
  isLoading = false;
  serviceKind: HelpServiceKind = "tow";

  isDrawerOpen = false;
  selectedUuid: string | null = null;
  selectedOrder: HelpOrderDetail | null = null;
  isDetailLoading = false;
  isSaving = false;
  isStatusUpdating = false;
  isNotifying = false;
  isLatSending = false;
  isLatSyncing = false;

  catalog: HelpService[] = [];
  executors: HelpExecutorProfile[] = [];

  commentText = "";
  isCommentSubmitting = false;

  isEditing = false;
  editDraft: HelpOrderSaveDraft | null = null;
  discardIntent: "cancel" | "close" | "open_client" | null = null;
  private pendingClientUuid: string | null = null;

  private editBaseline: HelpOrderSaveDraft | null = null;
  private sessionId = 0;

  constructor() {
    makeAutoObservable(this);
  }

  get isEditDirty(): boolean {
    if (!this.isEditing || !this.editDraft || !this.editBaseline) return false;
    return snapshotKey(this.editDraft) !== snapshotKey(this.editBaseline);
  }

  get editTotal(): number {
    if (!this.editDraft) return 0;
    return orderTotal({ items: this.editDraft.items });
  }

  get editExtraAmount(): number {
    if (!this.editDraft) return 0;
    return extraOnSiteAmount({
      items: this.editDraft.items,
      prepaidAmount: this.editDraft.prepaidAmount,
    });
  }

  private bumpSession = () => {
    this.sessionId += 1;
  };

  private enqueueWrite = <T,>(task: () => Promise<T>): Promise<T> => {
    const run = helpWriteChain.then(task, task);
    helpWriteChain = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  };

  private resetEditor = () => {
    this.isEditing = false;
    this.editDraft = null;
    this.editBaseline = null;
    this.discardIntent = null;
    this.pendingClientUuid = null;
    this.isSaving = false;
  };

  get visibleItems(): HelpOrderListItem[] {
    return this.items.filter((item) => item.kind === this.serviceKind);
  }

  get displayMetrics(): HelpMetricsView {
    const throughLat =
      this.metrics.through_lat > 0
        ? this.metrics.through_lat
        : countLatOrders(this.items, this.serviceKind);

    return {
      ...this.metrics,
      through_lat: throughLat,
    };
  }

  loadOrders = async () => {
    this.isLoading = true;

    try {
      const [listResponse, metricsResponse] = await Promise.all([
        superadminListHelpOrders(),
        superadminHelpOrdersMetrics().catch(() => null),
      ]);
      const { items, metrics: listMetrics } = parseListResponse(listResponse);
      const metrics =
        metricsResponse !== null
          ? parseMetrics(metricsResponse)
          : (listMetrics ?? emptyMetrics());

      runInAction(() => {
        this.items = items;
        this.metrics = metrics;
        this.isLoading = false;
      });
    } catch {
      runInAction(() => {
        this.items = [];
        this.metrics = emptyMetrics();
        this.isLoading = false;
      });
      toastStore.showError("Не удалось загрузить заявки помощи");
    }
  };

  loadCatalog = async () => {
    try {
      const [services, executors] = await Promise.all([
        superadminListHelpServices(),
        superadminListHelpExecutors(),
      ]);
      runInAction(() => {
        this.catalog = unwrapHelpList(services)
          .map(mapHelpService)
          .filter((item) => item.uuid);
        this.executors = unwrapHelpList(executors)
          .map(mapHelpExecutorProfile)
          .filter((item) => item.uuid);
      });
    } catch {
      toastStore.showError("Не удалось загрузить справочники помощи");
    }
  };

  openOrder = async (uuid: string) => {
    this.bumpSession();
    const session = this.sessionId;
    this.selectedUuid = uuid;
    this.isDrawerOpen = true;
    this.isDetailLoading = true;
    this.selectedOrder = null;
    this.commentText = "";
    this.resetEditor();
    void this.loadCatalog();

    try {
      const response = await superadminGetHelpOrder({ uuid });
      if (this.sessionId !== session) return;
      const detail = mapHelpOrderDetail(unwrapHelpPayload(response));
      runInAction(() => {
        if (this.sessionId !== session) return;
        this.selectedOrder = detail;
        this.isDetailLoading = false;
      });
      if (!detail) {
        toastStore.showError("Не удалось открыть заявку");
        this.closeDrawer();
      }
    } catch (error) {
      if (this.sessionId !== session) return;
      runInAction(() => {
        this.isDetailLoading = false;
      });
      toastStore.showError(extractApiError(error, "Не удалось загрузить заявку"));
      this.closeDrawer();
    }
  };

  closeDrawer = () => {
    this.bumpSession();
    this.isDrawerOpen = false;
    this.selectedUuid = null;
    this.selectedOrder = null;
    this.isDetailLoading = false;
    this.commentText = "";
    this.isStatusUpdating = false;
    this.isNotifying = false;
    this.isLatSending = false;
    this.isLatSyncing = false;
    this.isCommentSubmitting = false;
    this.resetEditor();
  };

  requestCloseDrawer = () => {
    if (this.isEditing && this.isEditDirty) {
      this.pendingClientUuid = null;
      this.discardIntent = "close";
      return;
    }
    this.closeDrawer();
  };

  /** Закрывает drawer и возвращает true, если можно сразу перейти к клиенту */
  requestOpenClient = (clientUuid: string): boolean => {
    if (this.isEditing && this.isEditDirty) {
      this.pendingClientUuid = clientUuid;
      this.discardIntent = "open_client";
      return false;
    }
    this.closeDrawer();
    return true;
  };

  enterEdit = () => {
    const order = this.selectedOrder;
    if (!order || this.isDetailLoading) return;
    const draft = createEditDraft(order);
    this.editDraft = draft;
    this.editBaseline = cloneDraft(draft);
    this.discardIntent = null;
    this.isEditing = true;
  };

  exitEdit = () => {
    this.resetEditor();
  };

  requestCancelEdit = () => {
    if (this.isEditDirty) {
      this.discardIntent = "cancel";
      return;
    }
    this.exitEdit();
  };

  confirmDiscard = (): string | null => {
    const intent = this.discardIntent;
    const clientUuid = this.pendingClientUuid;
    this.exitEdit();
    if (intent === "close" || intent === "open_client") {
      this.closeDrawer();
    }
    return intent === "open_client" ? clientUuid : null;
  };

  dismissDiscard = () => {
    this.discardIntent = null;
    this.pendingClientUuid = null;
  };

  patchEditDraft = (patch: Partial<HelpOrderSaveDraft>) => {
    if (!this.editDraft) return;
    this.editDraft = { ...this.editDraft, ...patch };
  };

  setEditItems = (items: HelpLineItem[]) => {
    if (!this.editDraft) return;
    const prevTotal = orderTotal({ items: this.editDraft.items });
    const nextTotal = orderTotal({ items });
    const prepaidSynced = this.editDraft.prepaidAmount === prevTotal;
    this.editDraft = {
      ...this.editDraft,
      items,
      prepaidAmount: prepaidSynced
        ? nextTotal
        : Math.min(this.editDraft.prepaidAmount, nextTotal),
    };
  };

  setCommentText = (value: string) => {
    this.commentText = value;
  };

  private isCurrentSession = (session: number, uuid?: string): boolean => {
    if (this.sessionId !== session) return false;
    if (uuid && this.selectedUuid !== uuid) return false;
    return this.isDrawerOpen;
  };

  private reloadDetail = async (
    uuid: string,
    session: number,
  ): Promise<boolean> => {
    const fresh = await superadminGetHelpOrder({ uuid });
    if (!this.isCurrentSession(session, uuid)) return false;
    const detail = mapHelpOrderDetail(unwrapHelpPayload(fresh));
    if (!detail) {
      toastStore.showError("Не удалось обновить карточку заявки");
      return false;
    }
    runInAction(() => {
      if (!this.isCurrentSession(session, uuid)) return;
      this.applyDetail(detail);
    });
    return true;
  };

  private commitDetail = async (
    uuid: string,
    response: unknown,
    session: number,
  ): Promise<boolean> => {
    if (!this.isCurrentSession(session, uuid)) return false;
    let detail = mapHelpOrderDetail(unwrapHelpPayload(response));
    if (!detail) {
      try {
        return await this.reloadDetail(uuid, session);
      } catch {
        detail = null;
      }
    }
    if (!detail) {
      toastStore.showError("Не удалось обновить карточку заявки");
      return false;
    }
    runInAction(() => {
      if (!this.isCurrentSession(session, uuid)) return;
      this.applyDetail(detail);
    });
    return this.isCurrentSession(session, uuid);
  };

  private buildSavePayload = (
    draft: HelpOrderSaveDraft,
    baseline: HelpOrderSaveDraft,
  ): UpdateHelpOrderDto => {
    const body: UpdateHelpOrderDto = {};

    if (snapshotKey(draft.customer) !== snapshotKey(baseline.customer)) {
      body.customer = {
        client_name: draft.customer.clientName.trim(),
        client_phone: toStoredHelpPhone(draft.customer.clientPhone),
        vehicle_make_model: draft.customer.vehicle.makeModel.trim(),
        vehicle_reg_number: draft.customer.vehicle.regNumber.trim(),
        vehicle_vin: draft.customer.vehicle.vin.trim() || null,
        vehicle_year: draft.customer.vehicle.year.trim() || null,
      } as UpdateHelpOrderDto["customer"];
    }

    if (
      snapshotKey(draft.pickup) !== snapshotKey(baseline.pickup) ||
      snapshotKey(draft.dropoff) !== snapshotKey(baseline.dropoff)
    ) {
      body.pickup = toPointDto(draft.pickup);
      body.dropoff = draft.dropoff ? toPointDto(draft.dropoff) : null;
    }

    const itemsChanged = snapshotKey(draft.items) !== snapshotKey(baseline.items);
    if (itemsChanged) {
      body.items = draft.items
        .filter((item) => item.title.trim())
        .map(toItemPatch);
      body.blocked_wheels = deriveBlockedWheels(draft.items);
      body.hard_to_access = deriveHardToAccess(draft.items);
    }

    if (draft.prepaidStatus !== baseline.prepaidStatus) {
      body.prepaid_status = draft.prepaidStatus;
    }
    if (draft.extraPaymentStatus !== baseline.extraPaymentStatus) {
      body.extra_payment_status = draft.extraPaymentStatus;
    }

    const draftExecutor = draft.executor?.name.trim()
      ? { ...draft.executor, name: draft.executor.name.trim() }
      : null;
    const baselineExecutor = baseline.executor?.name.trim()
      ? { ...baseline.executor, name: baseline.executor.name.trim() }
      : null;
    if (executorPatchSnapshot(draftExecutor) !== executorPatchSnapshot(baselineExecutor)) {
      body.executor = toExecutorPatch(draftExecutor);
    }

    if (itemsChanged || draft.prepaidAmount !== baseline.prepaidAmount) {
      (body as HelpOrderUpdateBody).prepaid_amount = draft.prepaidAmount;
    }

    return body;
  };

  private applyStatus = (status: HelpOrderStatus) => {
    const order = this.selectedOrder;
    if (!order) return;
    this.applyDetail({ ...order, status });
  };

  updateStatus = async (status: HelpOrderStatusUpdatable) => {
    const order = this.selectedOrder;
    if (!order || status === order.status || this.isEditing) return;
    const previous = order.status;
    const session = this.sessionId;
    const uuid = order.uuid;

    runInAction(() => {
      this.isStatusUpdating = true;
      this.applyStatus(status);
    });

    await this.enqueueWrite(async () => {
      if (!this.isCurrentSession(session, uuid)) return;
      try {
        const response = await superadminUpdateHelpOrderStatus({
          uuid,
          requestBody: {
            status: status as UpdateHelpOrderStatusDto["status"],
          },
        });
        if (!this.isCurrentSession(session, uuid)) return;
        const mapped = mapHelpOrderDetail(unwrapHelpPayload(response));
        runInAction(() => {
          if (!this.isCurrentSession(session, uuid)) return;
          if (mapped) {
            this.applyDetail({ ...mapped, status });
          } else {
            this.applyStatus(status);
          }
        });
        if (this.isCurrentSession(session, uuid)) {
          toastStore.showSuccess("Статус обновлён");
        }
      } catch (error) {
        runInAction(() => {
          if (this.isCurrentSession(session, uuid)) this.applyStatus(previous);
        });
        if (this.isCurrentSession(session, uuid)) {
          toastStore.showError(
            extractApiError(error, "Не удалось сменить статус заявки"),
          );
        }
      } finally {
        runInAction(() => {
          if (this.sessionId === session) this.isStatusUpdating = false;
        });
      }
    });
  };

  updatePaymentStatus = async (
    field: "prepaid" | "extra",
    status: HelpPaymentStatus,
  ) => {
    const order = this.selectedOrder;
    if (!order || this.isEditing) return;
    const current = field === "prepaid" ? order.prepaidStatus : order.extraPaymentStatus;
    if (current === status) return;
    const previous = current;
    const session = this.sessionId;
    const uuid = order.uuid;

    runInAction(() => {
      this.applyDetail({
        ...order,
        prepaidStatus: field === "prepaid" ? status : order.prepaidStatus,
        extraPaymentStatus: field === "extra" ? status : order.extraPaymentStatus,
      });
    });

    await this.enqueueWrite(async () => {
      if (!this.isCurrentSession(session, uuid)) return;
      try {
        const response = await superadminUpdateHelpOrder({
          uuid,
          requestBody:
            field === "prepaid"
              ? { prepaid_status: status }
              : { extra_payment_status: status },
        });
        if (!this.isCurrentSession(session, uuid)) return;
        const mapped = mapHelpOrderDetail(unwrapHelpPayload(response));
        if (mapped) {
          runInAction(() => {
            if (!this.isCurrentSession(session, uuid)) return;
            this.applyDetail({
              ...mapped,
              prepaidStatus:
                field === "prepaid" ? status : mapped.prepaidStatus,
              extraPaymentStatus:
                field === "extra" ? status : mapped.extraPaymentStatus,
            });
          });
        } else {
          await this.commitDetail(uuid, response, session);
        }
      } catch (error) {
        runInAction(() => {
          if (!this.isCurrentSession(session, uuid)) return;
          this.applyDetail({
            ...order,
            prepaidStatus: field === "prepaid" ? previous : order.prepaidStatus,
            extraPaymentStatus:
              field === "extra" ? previous : order.extraPaymentStatus,
          });
        });
        if (!this.isCurrentSession(session, uuid)) return;
        toastStore.showError(
          extractApiError(error, "Не удалось обновить статус оплаты"),
        );
      }
    });
  };

  updateExecutorEta = async (etaMinutes: number | null) => {
    const order = this.selectedOrder;
    if (!order?.executor || this.isEditing) return;
    if (order.executor.etaMinutes === etaMinutes) return;
    const session = this.sessionId;
    const uuid = order.uuid;

    await this.enqueueWrite(async () => {
      if (!this.isCurrentSession(session, uuid)) return;
      try {
        const response = await superadminUpdateHelpOrder({
          uuid,
          requestBody: {
            executor: {
              eta_minutes: etaMinutes,
            } as UpdateHelpOrderDto["executor"],
          },
        });
        if (!this.isCurrentSession(session, uuid)) return;
        await this.commitDetail(uuid, response, session);
      } catch (error) {
        if (!this.isCurrentSession(session, uuid)) return;
        toastStore.showError(
          extractApiError(error, "Не удалось обновить время прибытия"),
        );
      }
    });
  };

  saveEdit = async (): Promise<boolean> => {
    const order = this.selectedOrder;
    const draft = this.editDraft;
    const baseline = this.editBaseline;
    if (!order || !draft || !baseline || this.isSaving) return false;

    if (!this.isEditDirty) {
      this.exitEdit();
      return true;
    }

    const executorName = draft.executor?.name.trim();
    if (executorName && !toStoredHelpPhone(draft.executor?.phone ?? "")) {
      toastStore.showError("Укажите телефон исполнителя");
      return false;
    }

    const session = this.sessionId;
    const uuid = order.uuid;
    const payload = this.buildSavePayload(draft, baseline);
    if (Object.keys(payload).length === 0) {
      this.exitEdit();
      return true;
    }

    this.isSaving = true;
    const draftSnapshot = cloneDraft(draft);

    return this.enqueueWrite(async () => {
      if (!this.isCurrentSession(session, uuid)) return false;
      try {
        const send = (requestBody: HelpOrderUpdateBody) =>
          superadminUpdateHelpOrder({
            uuid,
            requestBody: requestBody as UpdateHelpOrderDto,
          });
        const body = payload as HelpOrderUpdateBody;
        const prepaidAmountChanged =
          draftSnapshot.prepaidAmount !== baseline.prepaidAmount;
        let prepaidAmountPersisted = !prepaidAmountChanged;
        let response: unknown;

        try {
          response = await send(body);
        } catch (error) {
          if (body.prepaid_amount == null || !isPrepaidAmountUnsupportedError(error)) {
            throw error;
          }
          const { prepaid_amount: _prepaidAmount, ...rest } = body;
          if (Object.keys(rest).length === 0) {
            if (!this.selectedOrder) throw error;
            runInAction(() => {
              this.applyDetail(
                overlayDraftOnDetail(this.selectedOrder!, draftSnapshot),
              );
              this.isSaving = false;
              this.resetEditor();
            });
            toastStore.showWarning(
              "Сумма предоплаты пока не сохраняется на сервере. Статусы оплаты и позиции чека сохраняются.",
            );
            return true;
          }
          response = await send(rest);
          prepaidAmountPersisted = false;
        }

        if (!this.isCurrentSession(session, uuid)) return false;

        let detail: HelpOrderDetail | null = mapHelpOrderDetail(
          unwrapHelpPayload(response),
        );
        if (!detail) {
          try {
            const fresh = await superadminGetHelpOrder({ uuid });
            if (!this.isCurrentSession(session, uuid)) return false;
            detail = mapHelpOrderDetail(unwrapHelpPayload(fresh));
          } catch {
            detail = this.selectedOrder;
          }
        }
        if (!detail) {
          toastStore.showError("Не удалось обновить карточку заявки");
          runInAction(() => {
            this.isSaving = false;
          });
          return false;
        }

        runInAction(() => {
          this.applyDetail(overlayDraftOnDetail(detail, draftSnapshot));
          this.isSaving = false;
          this.resetEditor();
        });
        if (!prepaidAmountPersisted) {
          toastStore.showWarning(
            "Сумма предоплаты пока не сохраняется на сервере — обновлены только статусы оплаты.",
          );
        } else {
          toastStore.showSuccess("Изменения сохранены");
        }
        return true;
      } catch (error) {
        if (!this.isCurrentSession(session, uuid)) return false;
        runInAction(() => {
          this.isSaving = false;
        });
        toastStore.showError(
          extractApiError(error, "Не удалось сохранить заявку"),
        );
        return false;
      }
    });
  };

  addComment = async (): Promise<boolean> => {
    const order = this.selectedOrder;
    if (!order || this.isCommentSubmitting) return false;
    const dto = Object.assign(new HelpCommentDto(), { text: this.commentText });
    const errors = await validate(dto);
    if (errors.length > 0) return false;

    const text = this.commentText.trim();
    const session = this.sessionId;
    const uuid = order.uuid;
    this.isCommentSubmitting = true;

    return this.enqueueWrite(async () => {
      if (!this.isCurrentSession(session, uuid)) return false;
      try {
        const response = await superadminCommentHelpOrder({
          uuid,
          requestBody: { text },
        });
        if (!this.isCurrentSession(session, uuid)) return false;
        const ok = await this.commitDetail(uuid, response, session);
        runInAction(() => {
          if (this.sessionId !== session) return;
          if (ok) this.commentText = "";
          this.isCommentSubmitting = false;
        });
        if (ok) toastStore.showSuccess("Комментарий добавлен");
        return ok;
      } catch (error) {
        runInAction(() => {
          if (this.sessionId === session) this.isCommentSubmitting = false;
        });
        if (this.isCurrentSession(session, uuid)) {
          toastStore.showError(
            extractApiError(error, "Не удалось добавить комментарий"),
          );
        }
        return false;
      }
    });
  };

    notifyClient = async (text: string): Promise<boolean> => {
    const order = this.selectedOrder;
    const message = text.trim();
    if (!order || !message || this.isNotifying) return false;
    const session = this.sessionId;
    const uuid = order.uuid;
    this.isNotifying = true;

    return this.enqueueWrite(async () => {
      if (!this.isCurrentSession(session, uuid)) return false;
      try {
        const response = await superadminNotifyHelpOrder({
          uuid,
          requestBody: { text: message },
        });
        if (!this.isCurrentSession(session, uuid)) return false;
        const ok = await this.commitDetail(uuid, response, session);
        runInAction(() => {
          if (this.sessionId === session) this.isNotifying = false;
        });
        if (ok) toastStore.showSuccess("Уведомление отправлено");
        return ok;
      } catch (error) {
        runInAction(() => {
          if (this.sessionId === session) this.isNotifying = false;
        });
        if (this.isCurrentSession(session, uuid)) {
          toastStore.showError(
            extractApiError(error, "Не удалось отправить уведомление"),
          );
        }
        return false;
      }
    });
  };

  sendToLat = async (): Promise<boolean> => {
    const order = this.selectedOrder;
    if (!order || this.isLatSending || this.isEditing) return false;
    const session = this.sessionId;
    const uuid = order.uuid;
    this.isLatSending = true;

    return this.enqueueWrite(async () => {
      try {
        if (!this.isCurrentSession(session, uuid)) return false;
        const response = await superadminSendHelpOrderToLat(uuid);
        if (!this.isCurrentSession(session, uuid)) return false;
        const ok = await this.commitDetail(uuid, response, session);
        if (ok) toastStore.showSuccess("Заявка отправлена в ЛАТ");
        return ok;
      } catch (error) {
        if (this.isCurrentSession(session, uuid)) {
          toastStore.showError(
            extractApiError(error, "Не удалось отправить заявку в ЛАТ"),
          );
        }
        return false;
      } finally {
        runInAction(() => {
          if (this.sessionId === session) this.isLatSending = false;
        });
      }
    });
  };

  saveLatManual = async (input: {
    latOrderId: string;
    latStatusRaw: string;
    latShortLink: string;
  }): Promise<boolean> => {
    const order = this.selectedOrder;
    if (!order || this.isEditing) return false;
    const session = this.sessionId;
    const uuid = order.uuid;
    const requestBody = {
      lat_order_id: input.latOrderId.trim() || null,
      lat_status_raw: input.latStatusRaw.trim() || null,
      lat_short_link: input.latShortLink.trim() || null,
    } as UpdateHelpOrderDto;

    return this.enqueueWrite(async () => {
      if (!this.isCurrentSession(session, uuid)) return false;
      try {
        const response = await superadminUpdateHelpOrder({
          uuid,
          requestBody,
        });
        if (!this.isCurrentSession(session, uuid)) return false;
        const ok = await this.commitDetail(uuid, response, session);
        if (ok) toastStore.showSuccess("Данные ЛАТ сохранены");
        return ok;
      } catch (error) {
        if (this.isCurrentSession(session, uuid)) {
          toastStore.showError(
            extractApiError(error, "Не удалось сохранить данные ЛАТ"),
          );
        }
        return false;
      }
    });
  };

  syncFromLat = async (): Promise<boolean> => {
    const order = this.selectedOrder;
    if (!order || this.isLatSyncing || this.isEditing) return false;
    const session = this.sessionId;
    const uuid = order.uuid;
    this.isLatSyncing = true;

    return this.enqueueWrite(async () => {
      try {
        if (!this.isCurrentSession(session, uuid)) return false;
        const response = await superadminSyncHelpOrderFromLat(uuid);
        if (!this.isCurrentSession(session, uuid)) return false;
        const ok = await this.commitDetail(uuid, response, session);
        if (ok) toastStore.showSuccess("Заявка синхронизирована с ЛАТ");
        return ok;
      } catch (error) {
        if (this.isCurrentSession(session, uuid)) {
          toastStore.showError(
            extractApiError(error, "Не удалось синхронизировать заявку с ЛАТ"),
          );
        }
        return false;
      } finally {
        runInAction(() => {
          if (this.sessionId === session) this.isLatSyncing = false;
        });
      }
    });
  };

  private applyDetail(detail: HelpOrderDetail | null) {
    if (!detail) return;
    this.selectedOrder = detail;
    this.items = this.items.map((item) =>
      item.uuid === detail.uuid
        ? {
            ...item,
            client_name: detail.clientName,
            client_phone: detail.clientPhone,
            vehicle_reg_number: detail.vehicle.regNumber,
            status: detail.status,
            service_title: detail.serviceTitle,
            total_amount: detail.totalAmount || orderTotal(detail),
            prepaid_status: detail.prepaidStatus,
            provider: detail.provider,
          }
        : item,
    );
  }
}

export const helpStore = new HelpStore();
export type { HelpStore };
